import DownloadIcon from '@apps-common/ui-icons/Download.jsx';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {Box, Button, Collapse} from '@mui/material';
import {bool, func, object} from 'prop-types';

import React, {useCallback, useState} from 'react';

import MapPopperButton from '../MapPopperButton.jsx';
import {MeasureIndicator} from '../MeasureIndicator.jsx';
import {VisibilityControl} from './VisibilityControl.jsx';
import {aoiTooltipMap} from './tooltips.js';
import {MAP_TOOLS} from '@app/constants';
import {getExtent} from '@app/util/geom';

export const FeatureItem = ({
  onDelete,
  onDownload,
  onSave,
  onEdit,
  onZoomTo,
  isEditing,
  feature,
  tempGeometry,
  canToggleVisibility,
  onChangeVisibility,
  tooltips = aoiTooltipMap,
  onHover,
}) => {
  const [forceCollapse, setForceCollapse] = useState(false);

  const onClickDelete = useCallback(() => {
    // fold the display then delete.
    setForceCollapse(true);
    setTimeout(() => {
      onDelete(feature);
    }, 300);
  }, [onDelete, feature, setForceCollapse]);

  const onClickSave = useCallback(() => {
    onSave(feature);
  }, [onSave, feature]);

  const onClickEdit = useCallback(() => {
    onEdit(feature);
  }, [onEdit, feature]);

  const onClickVisToggle = useCallback(() => {
    onChangeVisibility(feature);
  }, [onChangeVisibility, feature]);

  const onClickDownload = useCallback(() => {
    onDownload(feature);
  }, [onDownload, feature]);

  // do not trigger enter/leave events
  //  while editing.
  const enterFeature = useCallback(() => {
    if (!isEditing) {
      onHover(feature);
    }
  }, [isEditing, onHover, feature]);

  const leaveFeature = useCallback(() => {
    if (!isEditing) {
      onHover();
    }
  }, [isEditing, onHover]);

  const zoomToFeature = useCallback(() => {
    // get the bounding box for the feature
    if (onZoomTo) {
      const extent = getExtent(feature.geometry);
      onZoomTo([extent.left, extent.bottom, extent.right, extent.top]);
    }
  }, [feature, onZoomTo]);

  return (
    <Collapse in={!forceCollapse && (!!feature || !!tempGeometry)}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        {canToggleVisibility && (
          <VisibilityControl
            visible={feature.properties?.visible !== false}
            isEditing={isEditing}
            onClick={onClickVisToggle}
          />
        )}
        <Box
          sx={theme => ({
            alignItems: 'center',
            backgroundColor:
              feature?.properties?.hover === true || isEditing
                ? `rgba(${(theme.vars || theme).palette.primary.mainChanel} / ${(theme.vars || theme).palette.action.selectedOpacity})`
                : 'rgba(0,0,0,0)',
            borderRadius: theme.spacing(0.25),
            display: 'flex',
            flex: 1,
            height: 56, // height of icon + icon top padding + icon bottom padding
            padding: theme.spacing(1),
            transition: 'background-color 500ms',
          })}
          onMouseEnter={enterFeature}
          onMouseLeave={leaveFeature}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {isEditing && tempGeometry ? (
              <MeasureIndicator
                geometry={tempGeometry}
                onClick={zoomToFeature}
                iconSize="small"
              />
            ) : (
              <MeasureIndicator
                geometry={feature?.geometry}
                onClick={zoomToFeature}
                iconSize="small"
              />
            )}
          </Box>
          {!isEditing && (
            <React.Fragment>
              <MapPopperButton
                tooltip="Download as GeoJSON"
                onClick={onClickDownload}
                data-testid="download-maptool"
              >
                <DownloadIcon />
              </MapPopperButton>

              <MapPopperButton
                tooltip={tooltips[MAP_TOOLS.EDIT]}
                tooltipPosition="top-end"
                onClick={onClickEdit}
              >
                <EditIcon />
              </MapPopperButton>
              <MapPopperButton
                data-testid="clear-aoi"
                onClick={onClickDelete}
                tooltip={tooltips.CLEAR}
                tooltipPosition="top-end"
              >
                <DeleteIcon />
              </MapPopperButton>
            </React.Fragment>
          )}

          {isEditing && feature && (
            <Button
              variant="contained"
              color="primary"
              size="mini"
              onClick={onClickSave}
            >
              Save
            </Button>
          )}
        </Box>
      </Box>
    </Collapse>
  );
};

FeatureItem.propTypes = {
  onDelete: func,
  onDownload: func,
  onEdit: func,
  onSave: func,
  onHover: func,
  onZoomTo: func,
  onChangeVisibility: func,
  isEditing: bool,
  canToggleVisibility: bool,
  tooltips: object,
  feature: object,
  tempGeometry: object,
};
